import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Component, Output, Input, NgModule } from '@angular/core';
import { get } from 'scriptjs';
import 'zone.js/dist/zone';
import * as i3 from '@angular/forms';
import { FormsModule } from '@angular/forms';
import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';

class PdfLibService {
    constructor() { }
}
/** @nocollapse */ PdfLibService.ɵfac = function PdfLibService_Factory(t) { return new (t || PdfLibService)(); };
/** @nocollapse */ PdfLibService.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: PdfLibService, factory: PdfLibService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PdfLibService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return []; }, null); })();

class SelectedAnnotation {
    constructor(values) {
        this.annotation = null;
        this.replies = [];
        this.idSelectedToUpdate = null;
        Object.assign(this, values);
    }
    get hasReplies() {
        return this.replies.length > 0;
    }
    getAnnotationById(id) {
        if (id === this.annotation.id) {
            return this.annotation;
        }
        else {
            // look in replies
            return this.replies.find(a => a.id === id);
        }
    }
    getAnnotationToUpdate() {
        if (this.idSelectedToUpdate !== null) {
            return this.getAnnotationById(this.idSelectedToUpdate);
        }
        else {
            return null;
        }
    }
    // Checks that two objects are the same apart from a list of properties to disregard
    // Note that the function is pretty basic, not foolproof. We could replace it with a LoDash one to make it safer to use.
    objectSameApartFrom(keysToDisregard, obj1, obj2) {
        return Object.keys(obj1)
            .filter((key) => keysToDisregard.indexOf(key) === -1)
            .every((key) => JSON.stringify(obj1[key]) === JSON.stringify(obj2[key]));
    }
    isITargetNode(toBeDetermined) {
        return toBeDetermined.selector !== undefined;
    }
    // Checks to see if the annotation passed is basically the same as the last one undone
    // (Some properties, such as the 'modified' data property are disregarded in the comparison)
    isSameAs(annotation) {
        const lastAnnotationUndone = this.getAnnotationToUpdate();
        if (!lastAnnotationUndone)
            return false;
        // Disregard the 'modified' date property when comparing the annotations.
        // Also disregard boundingBox and quadPoints which can change a little even when the user does nothing!
        // (Unfortunately these are nested properties, making things more long-winded)
        if (this.isITargetNode(annotation.target) && this.isITargetNode(lastAnnotationUndone.target)) {
            const selectorsSame = this.objectSameApartFrom(['boundingBox', 'quadPoints'], annotation.target.selector, lastAnnotationUndone.target.selector);
            if (!selectorsSame)
                return false;
            const targetsSame = this.objectSameApartFrom(['selector'], annotation.target, lastAnnotationUndone.target);
            if (!targetsSame)
                return false;
            return this.objectSameApartFrom(['modified', 'target'], annotation, lastAnnotationUndone);
        }
        return this.objectSameApartFrom(['modified'], annotation, lastAnnotationUndone);
    }
    removeReply(annotation) {
        const sourceId = annotation.target.source;
        if (this.annotation.id === sourceId) {
            const index = this.replies.findIndex(r => r.id === annotation.id);
            if (index !== -1) {
                this.replies.splice(index, 1);
            }
        }
    }
}

class ApiEvent {
    constructor(values) {
        Object.assign(this, values);
    }
    get isTypeToIntercept() {
        return ['ANNOTATION_SELECTED', 'ANNOTATION_UPDATED', 'ANNOTATION_DELETED'].includes(this.type);
    }
}

// Helper function to check if selected file is PDF or not.
function isValidPDF(file) {
    if (file.type === 'application/pdf') {
        return true;
    }
    if (file.type === '' && file.name) {
        const fileName = file.name;
        const lastDotIndex = fileName.lastIndexOf('.');
        if (lastDotIndex === -1 || fileName.substr(lastDotIndex).toUpperCase() !== 'PDF') {
            return false;
        }
        return true;
    }
    return false;
}
function isNonEmptyArrayOfStrings(value) {
    return Array.isArray(value) && value.length > 0 && value.every(item => typeof item === "string");
}

class AnnotationInfo {
    constructor(values) {
        this.childIds = new Set(); // set of ids of replies
        this.type = null;
        this.annotation = null;
        if (values) {
            Object.assign(this, values);
        }
    }
    clone() {
        return Object.assign({}, this);
    }
    // returns childIds as array to use on UI (because the Set<string> doesn't seem to work well with json pipe)
    get childIdArray() {
        return Array.from(this.childIds);
    }
    hasChildren() {
        return this.childIds.size > 0;
    }
    hasReplies() {
        return this.hasChildren();
    }
    addChildId(id) {
        this.childIds.add(id);
    }
    removeChildId(id) {
        this.childIds.delete(id);
    }
    get isReply() {
        return this.annotation.motivation === 'replying';
    }
}

// A record of information about annotations, containing their types and child ids.
class AnnotationsMap {
    constructor(fileId) {
        this.fileId = null;
        this.annotationMap = {};
        this.parentAnnotations = [];
        this.fileId = fileId;
    }
    store(typedAnnotation) {
        if (this.contains(typedAnnotation.annotation.id)) {
            this.updateAnnotationType(typedAnnotation.annotation, typedAnnotation.type);
        }
        else {
            this.annotationMap[typedAnnotation.annotation.id] = new AnnotationInfo({
                childIds: new Set(),
                type: typedAnnotation.type,
                annotation: typedAnnotation.annotation
            });
            if (typedAnnotation.annotation.motivation === 'commenting') {
                this.parentAnnotations.push({ annotation: typedAnnotation.annotation, type: typedAnnotation.type, replies: [] });
            }
        }
        this.addToParent(typedAnnotation);
    }
    addToParent(typedAnnotation) {
        const sourceId = typedAnnotation.annotation.target.source;
        const parentAnnotation = this.parentAnnotations.find(a => a.annotation.id === sourceId);
        if (parentAnnotation !== undefined) {
            const reply = parentAnnotation.replies.find(r => r.annotation.id === typedAnnotation.annotation.id);
            if (reply !== undefined) {
                reply.annotation = typedAnnotation.annotation;
                reply.type = typedAnnotation.type;
            }
            else {
                parentAnnotation.replies.push(typedAnnotation);
            }
        }
        // TO DELETE?...the rest here if we're just using parentAnnotations
        if (sourceId !== this.fileId) {
            let existingAnnotation = this.annotationMap[sourceId];
            if (!this.contains(sourceId)) {
                existingAnnotation = new AnnotationInfo();
            }
            this.annotationMap[sourceId] = new AnnotationInfo({
                childIds: existingAnnotation.childIds.add(typedAnnotation.annotation.id),
                type: typedAnnotation.type,
                annotation: existingAnnotation.annotation
            });
            //this.annotationMap[sourceId].addChildId(typedAnnotation.annotation.id);
            this.annotationMap = Object.assign({}, this.annotationMap);
        }
    }
    updateReply(reply) {
        const sourceId = reply.annotation.target.source;
        const parentAnnotation = this.parentAnnotations.find(r => r.annotation.id === sourceId);
        const replyFound = parentAnnotation.replies.find(r => r.annotation.id === reply.annotation.id);
        if (replyFound !== undefined) {
            replyFound.annotation = reply.annotation;
            replyFound.type = reply.type;
        }
        else {
            parentAnnotation.replies.push(reply);
        }
    }
    updateAnnotation(annotation) {
        const typedAnnotation = this.annotationMap[annotation.id];
        if (typedAnnotation !== undefined) {
            typedAnnotation.annotation = annotation;
            const parent = this.parentAnnotations.find(a => a.annotation.id === annotation.id);
            if (parent !== undefined) {
                parent.annotation = annotation;
            }
            else {
                this.updateReply(typedAnnotation);
            }
        }
    }
    updateAnnotationType(annotation, annotationType) {
        let typedAnnotation = this.annotationMap[annotation.id];
        if (typedAnnotation !== undefined) {
            typedAnnotation.type = annotationType;
            this.annotationMap = Object.assign({}, this.annotationMap);
            const parent = this.parentAnnotations.find(a => a.annotation.id === annotation.id);
            if (parent !== undefined) {
                parent.type = annotationType;
            }
        }
    }
    deleteAnnotation(annotation) {
        if (this.contains(annotation.id)) {
            const indexAsParent = this.parentAnnotations.findIndex(r => r.annotation.id === annotation.id);
            if (indexAsParent !== -1) {
                // delete child replies first
                for (const reply of this.parentAnnotations[indexAsParent].replies) {
                    //this.deleteAnnotation(reply.annotation); not necessary, we're going to delete whole parent anyway
                    delete this.annotationMap[reply.annotation.id];
                }
                this.parentAnnotations.splice(indexAsParent, 1);
            }
            else {
                this.removeFromSource(annotation);
            }
            delete this.annotationMap[annotation.id];
        }
    }
    removeFromSource(annotation) {
        const sourceId = annotation.target.source;
        if (this.contains(sourceId)) {
            this.annotationMap[sourceId].removeChildId(annotation.id);
            const parentAnnotation = this.parentAnnotations.find(a => a.annotation.id === sourceId);
            const index = parentAnnotation.replies.findIndex(r => r.annotation.id === annotation.id);
            if (index !== -1) {
                parentAnnotation.replies.splice(index, 1);
            }
        }
    }
    sortParentAnnotations() {
        // TODO: want to sort in some order, not sure what order yet!
        // Want to keep annotation and child replies together?
        this.parentAnnotations.sort((a, b) => {
            if (a.annotation.modified > b.annotation.modified) {
                return 1;
            }
            if (a.annotation.modified < b.annotation.modified) {
                return -1;
            }
            return 0;
        });
    }
    item(annotation) {
        if (!this.contains(annotation.id)) {
            return new AnnotationInfo();
        }
        else {
            return this.annotationMap[annotation.id];
        }
    }
    contains(annotationId) {
        return this.annotationMap[annotationId] !== undefined;
    }
    hasChildAnnotations(annotation) {
        return this.item(annotation).hasChildren();
    }
}

/*
Copyright 2020 Adobe
All Rights Reserved.

NOTICE: Adobe permits you to use, modify, and distribute this file in
accordance with the terms of the Adobe license agreement accompanying
it. If you have received this file from a source other than Adobe,
then your use, modification, or distribution of it requires the prior
written permission of Adobe.
*/

class PDFLibConfig {
    constructor() {
        this.locale = "en-US"; // other major languages also supported. e.g. ja-JP, zh-CN, zh-TW, ko-KR, en-GB, de-DE
    }
}

class ViewerService {
    constructor(pdfLibConfig) {
        this.pdfLibConfig = pdfLibConfig;
        this.viewerConfig = {
            /* Enable commenting APIs */
            enableAnnotationAPIs: true,
            enableFormFilling: true,
            showPageControls: true,
            dockPageControls: true,
            showDownloadPDF: true,
            showPrintPDF: true,
            showAnnotationTools: true,
            showLeftHandPanel: true,
            defaultViewMode: "FIT_WIDTH",
            enableSearchAPIs: true
        };
        this.readyPromise = new Promise((resolve) => {
            if (window.AdobeDC) {
                resolve(null);
            }
            else {
                /* Wait for Adobe Document Services PDF Embed API to be ready */
                document.addEventListener('adobe_dc_view_sdk.ready', () => {
                    resolve(null);
                });
            }
        });
    }
    ready() {
        return this.readyPromise;
    }
    get adobeDCView() {
        if (!this._adobeDCView) {
            /* Initialize the AdobeDC View object */
            this._adobeDCView = new window.AdobeDC.View({
                /* Pass your registered client id */
                /* clientId: '8c0cd670273d451cbc9b351b11d22318', */
                /*clientId: '62108e48dbf749b48638adac9e329455',*/
                clientId: this.pdfLibConfig.clientId,
                /* Pass the div id in which PDF should be rendered */
                divId: 'pdf-div',
                locale: this.pdfLibConfig.locale
            });
        }
        return this._adobeDCView;
    }
    resetAdobeDCView() {
        this._adobeDCView = null;
    }
    /* Helper function to render the file using PDF Embed API. */
    previewFileUsingFilePromise(divId, filePromise, fileName, fileId) {
        /* Invoke the file preview API on Adobe DC View object */
        return this.adobeDCView.previewFile({
            /* Pass information on how to access the file */
            content: {
                /* pass file promise which resolve to arrayBuffer */
                promise: filePromise,
            },
            metaData: {
                /* file name */
                fileName: fileName,
                /* file ID */
                id: fileId
            }
        }, this.viewerConfig);
    }
    registerSaveApiHandler() {
        /* Define Save API Handler */
        const saveApiHandler = (metaData, content, options) => {
            console.log(metaData, content, options);
            return new Promise((resolve) => {
                /* Dummy implementation of Save API, replace with your business logic */
                setTimeout(() => {
                    const response = {
                        code: window.AdobeDC.View.Enum.ApiResponseCode.SUCCESS,
                        data: {
                            metaData: Object.assign(metaData, { updatedAt: new Date().getTime() })
                        },
                    };
                    resolve(response);
                }, 2000);
            });
        };
        this.adobeDCView.registerCallback(window.AdobeDC.View.Enum.CallbackType.SAVE_API, saveApiHandler, {});
    }
    registerEventsHandler() {
        /* Register the callback to receive the events */
        this.adobeDCView.registerCallback(
        /* Type of call back */
        window.AdobeDC.View.Enum.CallbackType.EVENT_LISTENER, 
        /* call back function */
        (event) => {
            console.log(event);
        }, 
        /* options to control the callback execution */
        {
            /* Enable PDF analytics events on user interaction. */
            enablePDFAnalytics: true,
        });
    }
    setUserProfile(userProfile) {
        const profile = {
            userProfile: {
                name: userProfile.name,
                firstName: userProfile.firstName,
                lastName: userProfile.lastName,
                email: userProfile.emailAddress
            }
        };
        const getUserApiHandler = () => {
            console.log(profile);
            return new Promise((resolve, reject) => {
                console.log('getUserApiHandlerPromise');
                const success = {
                    code: window.AdobeDC.View.Enum.ApiResponseCode.SUCCESS,
                    data: profile
                };
                const failure = {
                    code: window.AdobeDC.View.Enum.ApiResponseCode.FAIL,
                    data: 'setting user details failed!!!'
                };
                // success
                resolve(success);
            });
        };
        const options = {};
        this.adobeDCView.registerCallback(window.AdobeDC.View.Enum.CallbackType.GET_USER_PROFILE_API, getUserApiHandler, options);
    }
}
/** @nocollapse */ ViewerService.ɵfac = function ViewerService_Factory(t) { return new (t || ViewerService)(i0.ɵɵinject(PDFLibConfig)); };
/** @nocollapse */ ViewerService.ɵprov = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjectable({ token: ViewerService, factory: ViewerService.ɵfac, providedIn: 'root' });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ViewerService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: PDFLibConfig }]; }, null); })();

function PdfLibComponent_div_1_li_6_option_16_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "option", 14);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const annotationType_r7 = ctx.$implicit;
    i0.ɵɵproperty("ngValue", annotationType_r7);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", annotationType_r7.name, " ");
} }
function PdfLibComponent_div_1_li_6_div_17_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtext(1, " Replies ");
    i0.ɵɵelementEnd();
} }
function PdfLibComponent_div_1_li_6_div_18_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div")(1, "h5");
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "em");
    i0.ɵɵtext(4);
    i0.ɵɵpipe(5, "date");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(6, "div")(7, "pre");
    i0.ɵɵtext(8);
    i0.ɵɵelementEnd()()();
} if (rf & 2) {
    const reply_r8 = ctx.$implicit;
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(reply_r8.annotation.creator.name);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind2(5, 3, reply_r8.annotation.modified, "dd/MM/yyyy h:mm:ss a z"));
    i0.ɵɵadvance(4);
    i0.ɵɵtextInterpolate(reply_r8.annotation.bodyValue);
} }
function PdfLibComponent_div_1_li_6_Template(rf, ctx) { if (rf & 1) {
    const _r10 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "li")(1, "div", 8);
    i0.ɵɵelement(2, "img", 9);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "div", 10)(4, "h5");
    i0.ɵɵtext(5);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(6, "h4");
    i0.ɵɵtext(7);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(8, "em");
    i0.ɵɵtext(9);
    i0.ɵɵpipe(10, "date");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(11, "div")(12, "pre");
    i0.ɵɵtext(13);
    i0.ɵɵelementEnd()();
    i0.ɵɵelementStart(14, "span", 11)(15, "select", 12);
    i0.ɵɵlistener("ngModelChange", function PdfLibComponent_div_1_li_6_Template_select_ngModelChange_15_listener($event) { const restoredCtx = i0.ɵɵrestoreView(_r10); const parentAnnotation_r3 = restoredCtx.$implicit; return i0.ɵɵresetView(parentAnnotation_r3.type = $event); })("ngModelChange", function PdfLibComponent_div_1_li_6_Template_select_ngModelChange_15_listener() { const restoredCtx = i0.ɵɵrestoreView(_r10); const parentAnnotation_r3 = restoredCtx.$implicit; const ctx_r11 = i0.ɵɵnextContext(2); return i0.ɵɵresetView(ctx_r11.onChangeSelectedAnnotationType(parentAnnotation_r3)); });
    i0.ɵɵtemplate(16, PdfLibComponent_div_1_li_6_option_16_Template, 2, 2, "option", 13);
    i0.ɵɵelementEnd()();
    i0.ɵɵtemplate(17, PdfLibComponent_div_1_li_6_div_17_Template, 2, 0, "div", 3);
    i0.ɵɵtemplate(18, PdfLibComponent_div_1_li_6_div_18_Template, 9, 6, "div", 7);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const parentAnnotation_r3 = ctx.$implicit;
    const ctx_r2 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(5);
    i0.ɵɵtextInterpolate(parentAnnotation_r3.annotation.creator.name);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(parentAnnotation_r3.annotation.target.selector.subtype);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(i0.ɵɵpipeBind2(10, 10, parentAnnotation_r3.annotation.modified, "dd/MM/yyyy h:mm:ss a z"));
    i0.ɵɵadvance(4);
    i0.ɵɵtextInterpolate(parentAnnotation_r3.annotation.bodyValue);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngModel", parentAnnotation_r3.type)("disabled", false)("compareWith", ctx_r2.compareAnnotationTypeFn);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", ctx_r2.annotationTypes);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", parentAnnotation_r3.replies.length > 0);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", parentAnnotation_r3.replies);
} }
function PdfLibComponent_div_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 4)(1, "h1");
    i0.ɵɵtext(2, "Annotations");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(3, "span", 5);
    i0.ɵɵtext(4, "Some file name");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "ul", 6);
    i0.ɵɵtemplate(6, PdfLibComponent_div_1_li_6_Template, 19, 13, "li", 7);
    i0.ɵɵelementEnd()();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(6);
    i0.ɵɵproperty("ngForOf", ctx_r0.annotationsMap.parentAnnotations);
} }
function PdfLibComponent_div_3_div_1_div_1_div_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ci_r19 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ci_r19, " ");
} }
function PdfLibComponent_div_3_div_1_div_1_div_10_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ci_r20 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ci_r20, " ");
} }
function PdfLibComponent_div_3_div_1_div_1_div_12_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ci_r21 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", ci_r21, " ");
} }
function PdfLibComponent_div_3_div_1_div_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtext(1);
    i0.ɵɵelement(2, "br");
    i0.ɵɵtext(3);
    i0.ɵɵpipe(4, "json");
    i0.ɵɵelement(5, "br");
    i0.ɵɵtext(6);
    i0.ɵɵpipe(7, "json");
    i0.ɵɵtemplate(8, PdfLibComponent_div_3_div_1_div_1_div_8_Template, 2, 1, "div", 7);
    i0.ɵɵtext(9, " Value5: ");
    i0.ɵɵtemplate(10, PdfLibComponent_div_3_div_1_div_1_div_10_Template, 2, 1, "div", 7);
    i0.ɵɵtext(11, " Value6: ");
    i0.ɵɵtemplate(12, PdfLibComponent_div_3_div_1_div_1_div_12_Template, 2, 1, "div", 7);
    i0.ɵɵtext(13);
    i0.ɵɵpipe(14, "json");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const item_r15 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" Key: ", item_r15.key, "");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" Value: ", i0.ɵɵpipeBind1(4, 8, item_r15.value), "");
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate2(" Value2: ", item_r15.value.childIds.size, " Value3: ", i0.ɵɵpipeBind1(7, 10, item_r15.value.childIds), " Value4: ");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngForOf", item_r15.value.childIds);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngForOf", item_r15.value.childIds.keys());
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngForOf", item_r15.value.childIds.values());
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" Value7: ", i0.ɵɵpipeBind1(14, 12, item_r15.value.childIdArray), " ");
} }
function PdfLibComponent_div_3_div_1_div_7_option_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "option", 14);
    i0.ɵɵtext(1);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const annotationType_r24 = ctx.$implicit;
    i0.ɵɵproperty("ngValue", annotationType_r24);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", annotationType_r24.name, " ");
} }
function PdfLibComponent_div_3_div_1_div_7_Template(rf, ctx) { if (rf & 1) {
    const _r26 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "json");
    i0.ɵɵelementStart(3, "select", 15);
    i0.ɵɵlistener("ngModelChange", function PdfLibComponent_div_3_div_1_div_7_Template_select_ngModelChange_3_listener($event) { const restoredCtx = i0.ɵɵrestoreView(_r26); const parentAnnotation_r22 = restoredCtx.$implicit; return i0.ɵɵresetView(parentAnnotation_r22.type = $event); })("ngModelChange", function PdfLibComponent_div_3_div_1_div_7_Template_select_ngModelChange_3_listener() { const restoredCtx = i0.ɵɵrestoreView(_r26); const parentAnnotation_r22 = restoredCtx.$implicit; const ctx_r27 = i0.ɵɵnextContext(3); return i0.ɵɵresetView(ctx_r27.onChangeSelectedAnnotationType(parentAnnotation_r22)); });
    i0.ɵɵtemplate(4, PdfLibComponent_div_3_div_1_div_7_option_4_Template, 2, 2, "option", 13);
    i0.ɵɵelementEnd();
    i0.ɵɵelement(5, "br");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const parentAnnotation_r22 = ctx.$implicit;
    const ctx_r14 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate6(" Key: ", parentAnnotation_r22.annotation.id, "... Modified: ", parentAnnotation_r22.annotation.modified, " ... Creator: ", parentAnnotation_r22.annotation.creator.name, " ...Text: ", parentAnnotation_r22.annotation.bodyValue, "... Motivation: ", parentAnnotation_r22.annotation.motivation, " ...Replies: ", i0.ɵɵpipeBind1(2, 10, parentAnnotation_r22.replies), " ");
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngModel", parentAnnotation_r22.type)("disabled", false)("compareWith", ctx_r14.compareAnnotationTypeFn);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", ctx_r14.annotationTypes);
} }
function PdfLibComponent_div_3_div_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtemplate(1, PdfLibComponent_div_3_div_1_div_1_Template, 15, 14, "div", 7);
    i0.ɵɵpipe(2, "keyvalue");
    i0.ɵɵelementStart(3, "div");
    i0.ɵɵtext(4);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(5, "div");
    i0.ɵɵtext(6);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(7, PdfLibComponent_div_3_div_1_div_7_Template, 6, 12, "div", 7);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r12 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", i0.ɵɵpipeBind2(2, 4, ctx_r12.annotationsMap.annotationMap, ctx_r12.lastUpdatedOrder));
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1("PA count: ", ctx_r12.annotationsMap.parentAnnotations.length, "");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1("annotationsMap: ", ctx_r12.annotationsMap.annotationMap, "");
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngForOf", ctx_r12.annotationsMap.parentAnnotations);
} }
function PdfLibComponent_div_3_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div");
    i0.ɵɵtemplate(1, PdfLibComponent_div_3_div_1_Template, 8, 7, "div", 3);
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", ctx_r1.annotationsMap);
} }
class PdfLibComponent {
    constructor(viewSDKClient, pdfLibConfig) {
        this.viewSDKClient = viewSDKClient;
        this.pdfLibConfig = pdfLibConfig;
        this.annotationAdded = new EventEmitter(); // New annotation added by user in this session.
        this.annotationUpdated = new EventEmitter(); // Annotation updated by user in this session.
        this.annotationDeleted = new EventEmitter(); // Annotation deleted by user in this session.
        // Annotation selected by the user that is not the user's.
        this.annotationSelectedNotMine = new EventEmitter();
        // Update has been undone (e.g. because the user attempted to update something that wasn't his).
        this.annotationUpdateUndone = new EventEmitter();
        // Delete was undone (e.g. because the user attempted to delete something that wasn't his). Note the undo could
        // restore annotations and replies if we attempted to delete a parent annotation.
        this.annotationDeleteUndone = new EventEmitter();
        // All raw annotation events {type: string, data: IAnnotation}. Avoid using to track additions, updates or deletes for reasons below:
        // E.g. 1. Loading an historic file annotation (from any user) emits an 'ANNOTATION_ADDED' event even though the user hasn't added it in the session.
        // E.g. 2: We sometimes need to add, remove or update items internally to keep the expected state (e.g. restoring annotations when users try to delete others' ones).
        // You get a more accurate picture if you use the specialised annotationAdded/annotationUpdated/annotationDeleted instead.
        this.annotationEvent = new EventEmitter();
        this.annotationError = new EventEmitter();
        this.operationsInfo = new EventEmitter();
        this.annotationTypeChanged = new EventEmitter();
        this.annotationTypes = [];
        this.selectedAnnotationType = null;
        this.lastSelectedAnnotation = null;
        this.loadedAnnotations = [];
        this.annotationsMap = null;
        this.previewFilePromise = null;
        this._userProfile = null;
        this.debugOn = false;
        this.lastUpdatedOrder = (a, b) => {
            // try to keep annotation & reply together?
            if (a.value.annotation.modified === b.value.annotation.modified) {
                return 0;
            }
            if (a.value.annotation.modified > b.value.annotation.modified) {
                return -1;
            }
            else {
                return 1;
            }
        };
    }
    set fileInfo(value) {
        this.viewSDKClient.resetAdobeDCView(); // using a new file, so we can't reuse the AdobeDCView
        this._fileInfo = value;
        this.onFileUpload(this._fileInfo);
    }
    get fileInfo() {
        return this._fileInfo;
    }
    set annotationsToLoad(values) {
        if (values) {
            if (isNonEmptyArrayOfStrings(values)) {
                const strings = values;
                this._annotationsToLoad = strings.map(s => JSON.parse(s));
            }
            else {
                this._annotationsToLoad = values;
            }
            this.loadedAnnotations.push(...this._annotationsToLoad);
            this.loadAnnotations(this._annotationsToLoad);
        }
    }
    set userProfile(value) {
        if (value) {
            this._userProfile = value;
            if (this._fileInfo && this._fileInfo.files) {
                this.viewSDKClient.setUserProfile(this._userProfile);
            }
        }
    }
    get userProfile() {
        return this._userProfile;
    }
    ngOnInit() {
        get("https://documentcloud.adobe.com/view-sdk/main.js", () => {
        });
    }
    ngOnChanges(changes) {
        // A set method will always be invoked before the ngOnChanges lifecycle hook.
        // This is because it’s “plain JavaScript” (well, TypeScript). ngOnChanges is also invoked before ngOnInit
    }
    loadAnnotations(typedAnnotations) {
        if (this.previewFilePromise && typedAnnotations && typedAnnotations.length > 0) {
            const annotations = typedAnnotations.map(ta => ta.annotation);
            this.previewFilePromise
                .then(adobeViewer => adobeViewer.getAnnotationManager())
                .then(annotationManager => annotationManager.addAnnotations(annotations))
                .then(() => {
                this.operationsInfo.emit("annotationsLoadedAndAdded");
                for (const annotation of annotations) {
                    const typedAnnotation = typedAnnotations.find(ta => ta.annotation.id === annotation.id);
                    this.operationsInfo.emit({ "operation": "annotationLoaded", "annotation": JSON.stringify(typedAnnotation) });
                    this.annotationsMap.store(typedAnnotation);
                }
            })
                .catch(error => {
                this.annotationError.emit(error);
                // E.g. {code: 'FAIL', message: 'Annotation already there for given id 43a78ed5-e02…19ea189f886, b3a12033-d57d-4405-9f54-51eb206e3b20'}
            });
        }
    }
    onFileUpload(fileInfo) {
        this.viewSDKClient.ready().then(() => {
            if (fileInfo && fileInfo.files && fileInfo.files.length > 0 && isValidPDF(fileInfo.files[0])) {
                const fileId = fileInfo.fileId;
                this.annotationsMap = new AnnotationsMap(fileId);
                const fileName = fileInfo.files[0].name;
                const reader = new FileReader();
                reader.onloadend = e => {
                    const filePromise = Promise.resolve(reader.result);
                    this.previewFilePromise = this.viewSDKClient.previewFileUsingFilePromise('pdf-div', filePromise, fileName, fileId);
                    if (this._userProfile) {
                        this.viewSDKClient.setUserProfile(this._userProfile);
                    }
                    //this.loadAnnotations(this._annotationsToLoad);
                    let promiseChain = this.previewFilePromise
                        .then(adobeViewer => adobeViewer.getAnnotationManager())
                        .then(annotationManager => {
                        annotationManager.registerEventListener((event) => {
                            this.annotationEvent.emit(event);
                            this.processApiEvent(new ApiEvent(event));
                        }, {
                            // If no event is passed in listenOn, then all annotation events will be received
                            listenOn: [ /* 'ANNOTATION_ADDED', 'ANNOTATION_CLICKED' */]
                        });
                        return annotationManager;
                    });
                    if (this._annotationsToLoad && this._annotationsToLoad.length > 0) {
                        const annotations = this._annotationsToLoad.map(ta => ta.annotation);
                        promiseChain = promiseChain
                            .then(annotationManager => annotationManager.addAnnotations(annotations))
                            .then(() => {
                            this.annotationError.emit("annotationsLoadedAndAdded");
                            for (const annotation of annotations) {
                                const typedAnnotation = this._annotationsToLoad.find(ta => ta.annotation.id === annotation.id);
                                this.operationsInfo.emit({ "operation": "annotationLoaded", "annotation": JSON.stringify(typedAnnotation) });
                                this.annotationsMap.store(typedAnnotation);
                                this.operationsInfo.emit({ "annotationsMap": this.annotationsMap });
                            }
                        });
                    }
                    promiseChain.then(result => {
                        this.operationsInfo.emit("fileUploadPromiseChainCompleted");
                    })
                        .catch(error => {
                        this.annotationError.emit(error);
                        // E.g. {code: 'FAIL', message: 'Annotation already there for given id 43a78ed5-e02…19ea189f886, b3a12033-d57d-4405-9f54-51eb206e3b20'}
                    });
                };
                reader.readAsArrayBuffer(fileInfo.files[0]);
            }
        });
    }
    processApiEvent(apiEvent) {
        if (this.isUnrestricted(apiEvent)) {
            this.processEvent(apiEvent);
        }
        else {
            this.processEventOnAnnotationThatIsNotMine(apiEvent);
        }
    }
    isUnrestricted(apiEvent) {
        return (this.pdfLibConfig.canEditOtherUserAnnotations || this.annotationIsMine(apiEvent.data) || !apiEvent.isTypeToIntercept);
    }
    processEvent(apiEvent) {
        switch (apiEvent.type) {
            case 'ANNOTATION_ADDED':
                if (!this.annotationHasComeFromLoad(apiEvent.data)) {
                    const annotationWithDefaultType = { annotation: apiEvent.data, type: this.annotationTypes[0] };
                    this.annotationsMap.store(annotationWithDefaultType);
                    this.annotationAdded.emit(apiEvent.data);
                    // Add a new reply to the lastSelected state so we can restore it if the parent is deleted
                    if (apiEvent.data.motivation === 'replying') {
                        this.lastSelectedAnnotation.replies.push(apiEvent.data);
                        this.operationsInfo.emit({ "lastSelectedAnnotation": this.lastSelectedAnnotation });
                    }
                    this.operationsInfo.emit({ "annotationsMap": this.annotationsMap });
                }
                break;
            case 'ANNOTATION_UPDATED':
                this.annotationsMap.updateAnnotation(apiEvent.data);
                this.annotationUpdated.emit(apiEvent.data);
                break;
            case 'ANNOTATION_DELETED':
                this.annotationsMap.deleteAnnotation(apiEvent.data);
                this.annotationDeleted.emit(apiEvent.data);
                // Delete the reply from the lastSelected state so any restore does not include a validly deleted item
                if (apiEvent.data.motivation === 'replying') {
                    this.lastSelectedAnnotation.removeReply(apiEvent.data);
                    this.operationsInfo.emit({ "lastSelectedAnnotation": this.lastSelectedAnnotation });
                }
                break;
            case 'ANNOTATION_SELECTED':
                this.displayAnnotationType(apiEvent.data);
                break;
            case 'ANNOTATION_UNSELECTED':
                this.selectedAnnotationType = null;
                break;
        }
    }
    processEventOnAnnotationThatIsNotMine(apiEvent) {
        //  Currently, we can't easily disallow updates. We could check for ANNOTATION_SELECT events and immediately
        //  deselect if the user has selected another user's annotation but, if we disallow select, users won't be able to
        //  add replies to another person's annotations (and they may not be able to see full details of an annotation too).
        //
        // WORKAROUND OPTIONS CONSIDERED
        //
        // A) Allow users to select any annotation but capture the state if the user selects another user's annotation.
        // If an annotation that does not belong to the current user, add it back in. On ANNOTATION_UPDATED events,
        // update the annotation back to what it originally was if it didn't belong to the user.
        // ISSUES - possible pitfalls with this:
        // 1. Check that we can add a deleted annotation back with the same ID as before. (We've got a saved version using that ID on the server).
        // 2. We'd need to re-add any child replies that would be deleted.
        // 3. We need to be careful not to raise the usual 'add/delete' events so users of our component don't invoke server calls to add/delete.
        // 4. We need to tell the user what's going on, that their changes were rejected.
        //
        // B) An alternative is to re-load the whole thing if the user changes another person's annotation.
        // ISSUES - possible pitfalls with this.
        // 1. We need to tell the user what's going on.
        // 2. It could be slower and more annoying?
        //
        // C) Another way would be to intercept events where the user had selected another user's annotation and pop-up our own modal. This could get clunky though...
        // D) Leave it up to user of component by raising a special event if the user selects another user's annotation. (Do this anyway for the warning...)
        //
        // WORKAROUND CHOSEN: OPTION A
        if (apiEvent.type === 'ANNOTATION_SELECTED') {
            this.displayAnnotationType(apiEvent.data);
            // Save details in case we need to undo something.
            this.saveSelectedAnnotation(apiEvent.data);
        }
        if (apiEvent.type === 'ANNOTATION_UPDATED') {
            if (!this.isAnUndoUpdate(apiEvent.data)) {
                this.undoUpdateAnnotation(apiEvent.data);
            }
        }
        if (apiEvent.type === 'ANNOTATION_DELETED') {
            this.undoDeleteAnnotation(apiEvent.data);
            // Note that undo will restore only replies that were present when the parent was first selected.
            // This is because the snapshot of the parent annotation is only taken on selection.
            // So, for example, if you select someone else's annotation, do 2 replies on it and then try to delete the parent
            // annotation, it will only restore the parent, not the 2 replies you did after selecting it.
        }
        if (apiEvent.type === 'ANNOTATION_UNSELECTED') {
            this.selectedAnnotationType = null;
        }
    }
    isAnUndoUpdate(annotation) {
        return this.lastSelectedAnnotation.isSameAs(annotation);
    }
    saveSelectedAnnotation(annotation) {
        // Save selected annotation and any child replies.
        // (Selecting a particular sub-annotation or reply doesn't trigger a separate selection event so we need to save all)
        if (this.annotationsMap.item(annotation).hasReplies()) {
            const filter = {
                annotationIds: Array.from(this.annotationsMap.item(annotation).childIds.values())
            };
            this.previewFilePromise.then(adobeViewer => {
                adobeViewer.getAnnotationManager().then(annotationManager => {
                    annotationManager.getAnnotations(filter)
                        .then((result) => {
                        this.lastSelectedAnnotation = new SelectedAnnotation({ annotation: annotation, replies: result, idSelectedToUpdate: null });
                        this.annotationSelectedNotMine.emit(this.lastSelectedAnnotation);
                    })
                        .catch(error => this.annotationError.emit(error));
                });
            });
        }
        else {
            this.lastSelectedAnnotation = new SelectedAnnotation({ annotation: annotation, replies: [], idSelectedToUpdate: null });
            this.annotationSelectedNotMine.emit(this.lastSelectedAnnotation);
        }
    }
    undoDeleteAnnotation(from) {
        let annotationsAndRepliesToPutBack;
        const parentAnnotationSelected = (this.lastSelectedAnnotation.annotation.id === from.id);
        if (parentAnnotationSelected) {
            annotationsAndRepliesToPutBack = [this.lastSelectedAnnotation.annotation, ...this.lastSelectedAnnotation.replies];
        }
        else {
            annotationsAndRepliesToPutBack = [this.lastSelectedAnnotation.replies.find(a => a.id === from.id)];
        }
        this.previewFilePromise.then(adobeViewer => {
            adobeViewer.getAnnotationManager().then(annotationManager => {
                annotationManager.addAnnotations(annotationsAndRepliesToPutBack)
                    .then(() => {
                    this.annotationDeleteUndone.emit({ attemptedDelete: from, undoneBackTo: annotationsAndRepliesToPutBack });
                }).catch((error) => {
                    this.annotationError.emit(error);
                });
            });
        });
    }
    undoUpdateAnnotation(from) {
        // In the case of an annotation with replies, the selected annotation is always the parent but we may be updating
        // one of its child replies and if so we need to find which one.
        this.lastSelectedAnnotation.idSelectedToUpdate = from.id;
        const annotationThatWasUpdated = this.lastSelectedAnnotation.getAnnotationToUpdate();
        this.operationsInfo.emit({ operation: "undoUpdate", "from": from, "to": annotationThatWasUpdated });
        this.previewFilePromise.then(adobeViewer => {
            adobeViewer.getAnnotationManager().then(annotationManager => {
                annotationManager.updateAnnotation(annotationThatWasUpdated)
                    .then(() => {
                    this.annotationUpdateUndone.emit({ attemptedUpdate: from, undoneBackTo: annotationThatWasUpdated });
                })
                    .catch(error => {
                    this.annotationError.emit(error);
                });
            });
        });
    }
    displayAnnotationType(annotation) {
        this.selectedAnnotationType = this.annotationsMap.item(annotation).type;
    }
    annotationHasComeFromLoad(annotation) {
        return (this.loadedAnnotations.some(loadedAnnotation => loadedAnnotation.annotation.id === annotation.id));
    }
    annotationIsMine(iAnnotation) {
        if (iAnnotation.creator === undefined) {
            return false;
        }
        if (iAnnotation.creator.id !== undefined) {
            return iAnnotation.creator.id === this.userProfile.emailAddress;
        }
        else {
            return (iAnnotation.creator.name !== undefined) && iAnnotation.creator.name === this.userProfile.name;
        }
    }
    compareAnnotationTypeFn(a, b) {
        return a && b && a.id === b.id;
    }
    // Type changed in dropdown. Change our record of this and relay the change on for client to save.
    onChangeSelectedAnnotationType(parentAnnotation) {
        this.annotationsMap.store({ annotation: parentAnnotation.annotation, type: parentAnnotation.type });
        this.annotationTypeChanged.emit({ annotationId: parentAnnotation.annotation.id, type: parentAnnotation.type });
    }
}
/** @nocollapse */ PdfLibComponent.ɵfac = function PdfLibComponent_Factory(t) { return new (t || PdfLibComponent)(i0.ɵɵdirectiveInject(ViewerService), i0.ɵɵdirectiveInject(PDFLibConfig)); };
/** @nocollapse */ PdfLibComponent.ɵcmp = /** @pureOrBreakMyCode */ i0.ɵɵdefineComponent({ type: PdfLibComponent, selectors: [["kah-pdf-lib"]], inputs: { annotationTypes: "annotationTypes", fileInfo: "fileInfo", annotationsToLoad: "annotationsToLoad", userProfile: "userProfile" }, outputs: { annotationAdded: "annotationAdded", annotationUpdated: "annotationUpdated", annotationDeleted: "annotationDeleted", annotationSelectedNotMine: "annotationSelectedNotMine", annotationUpdateUndone: "annotationUpdateUndone", annotationDeleteUndone: "annotationDeleteUndone", annotationEvent: "annotationEvent", annotationError: "annotationError", operationsInfo: "operationsInfo", annotationTypeChanged: "annotationTypeChanged" }, features: [i0.ɵɵNgOnChangesFeature], decls: 4, vars: 2, consts: [[1, "root"], ["class", "sidebar annotation", 4, "ngIf"], ["id", "pdf-div", 1, "content"], [4, "ngIf"], [1, "sidebar", "annotation"], [1, "file-name"], [1, "comments-list"], [4, "ngFor", "ngForOf"], [1, "user-img"], ["src", "https://goworks.ergoengineering.com/CM/CM/previewService/get/avatar?id=2377", "alt", ""], [1, "user-comment"], [1, "curr-status"], ["name", "choice", 3, "ngModel", "disabled", "compareWith", "ngModelChange"], [3, "ngValue", 4, "ngFor", "ngForOf"], [3, "ngValue"], ["name", "choice", 1, "form-control", 3, "ngModel", "disabled", "compareWith", "ngModelChange"]], template: function PdfLibComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵtemplate(1, PdfLibComponent_div_1_Template, 7, 1, "div", 1);
        i0.ɵɵelement(2, "div", 2);
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(3, PdfLibComponent_div_3_Template, 2, 1, "div", 3);
    } if (rf & 2) {
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", ctx.annotationsMap && ctx.annotationsMap.parentAnnotations);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", ctx.debugOn);
    } }, dependencies: [i3.NgSelectOption, i3.ɵNgSelectMultipleOption, i3.SelectControlValueAccessor, i3.NgControlStatus, i3.NgModel, i4.NgForOf, i4.NgIf, i4.JsonPipe, i4.DatePipe, i4.KeyValuePipe], styles: [".file-name[_ngcontent-%COMP%]{display:block;padding:7px;text-align:left;position:sticky;top:0;z-index:999}.content[_ngcontent-%COMP%]{left:385px;padding:40px;background:#eaeaea;border-left:1px solid #d3d3d3;border-top:1px solid #d3d3d3;bottom:0;min-width:670px;overflow:hidden;position:absolute;top:0;transition:all .5s;width:calc(100vw - 466px);z-index:1}.sidebar[_ngcontent-%COMP%]{background-color:#f8f8f8;color:#4b4b4b;height:100%;overflow-y:auto;padding:25px 20px 0 25px}.sidebar[_ngcontent-%COMP%] > title[_ngcontent-%COMP%]{background:#fff;border-bottom:1px solid #d3d3d3;font-size:13px;letter-spacing:1px;padding:28px 0;text-align:center;text-transform:uppercase}.sidebar[_ngcontent-%COMP%] > main[_ngcontent-%COMP%]{height:calc(100% - 174px);overflow-y:auto;padding:25px 20px 0 25px}.container[_ngcontent-%COMP%]{width:385px;border-top:1px solid #d3d3d3;height:calc(100vh - 55px);position:relative}.root[_ngcontent-%COMP%]{border-top:1px solid #d3d3d3;height:calc(100vh - 55px);position:relative;width:385px}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]{float:left;width:100%!important;margin:0;padding:0 0 5%;list-style:none;flex-direction:column}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]{float:left;width:100%;padding:5px;margin-top:10px;border-bottom:solid 1px #dadde1;position:relative}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .user-img[_ngcontent-%COMP%]{float:left;width:45px;height:45px;border-radius:50%;overflow:hidden}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .user-comment[_ngcontent-%COMP%]{width:80%!important;margin-bottom:5px;position:relative;display:inline-block;padding:10px 0 0 10px}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .user-comment[_ngcontent-%COMP%]   h5[_ngcontent-%COMP%]{display:block;width:100%;font-size:15px;color:#263238;font-weight:600;text-transform:capitalize;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin:0 0 3px}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .user-comment[_ngcontent-%COMP%]   em[_ngcontent-%COMP%]{display:block;font-size:12px;color:var(--color-medium-grey);font-style:italic;text-align:left}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .user-comment[_ngcontent-%COMP%]   p[_ngcontent-%COMP%]{display:block;font-size:13px;color:#767676;line-height:15px;word-wrap:break-word}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   pre[_ngcontent-%COMP%]{display:block;font-size:14px;color:#767676;line-height:16px;word-wrap:break-word;margin:10px 0}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .user-comment[_ngcontent-%COMP%]   .edit-btn1[_ngcontent-%COMP%]{position:absolute;top:20px;right:0;margin:0;font-size:18px;color:#767676;word-wrap:break-word;cursor:pointer}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .user-comment[_ngcontent-%COMP%]   .edit-btn1[_ngcontent-%COMP%]   span[_ngcontent-%COMP%]   i[_ngcontent-%COMP%]{color:#000;font-size:medium}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .user-comment[_ngcontent-%COMP%]   #commentSpan[_ngcontent-%COMP%]{margin:0!important;position:static!important}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .user-comment[_ngcontent-%COMP%]   .save-reposition-active[_ngcontent-%COMP%]{position:static;top:inherit;left:inherit}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .user-comment[_ngcontent-%COMP%]   .status[_ngcontent-%COMP%]{float:left;font-size:12px;color:var(--color-white);padding:5px 10px;line-height:12px;border:none;border-radius:0;width:auto;height:auto}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .user-comment[_ngcontent-%COMP%]   .status.green[_ngcontent-%COMP%]{background:#2eb650}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .user-comment[_ngcontent-%COMP%]   .status.grey[_ngcontent-%COMP%]{background:var(--color-medium-grey)}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .user-comment[_ngcontent-%COMP%] + div[_ngcontent-%COMP%]{flex:0 0 100%;padding:5px;margin-bottom:10px;font-weight:700!important}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .user-comment[_ngcontent-%COMP%]   .col-md-7[_ngcontent-%COMP%]{width:100%;padding-left:0}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li.active[_ngcontent-%COMP%]   .user-img[_ngcontent-%COMP%]{border:solid 2px var(--primary);margin-bottom:10px;position:relative}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li.active[_ngcontent-%COMP%]   .user-img[_ngcontent-%COMP%]   img[_ngcontent-%COMP%]{height:100%;margin:auto;position:absolute;left:50%;transform:translate(-50%)}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li.active[_ngcontent-%COMP%]   .user-comment[_ngcontent-%COMP%]   h5[_ngcontent-%COMP%]{color:var(--primary);text-align:left}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .curr-status[_ngcontent-%COMP%]{position:absolute;right:5px;top:-5px;font-size:12px;color:#263238;padding:0}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li[_ngcontent-%COMP%]   .curr-status[_ngcontent-%COMP%]   select[_ngcontent-%COMP%]{border:solid 1px #ccc}.annotation[_ngcontent-%COMP%]   .comments-list[_ngcontent-%COMP%]   li.moderation-comment[_ngcontent-%COMP%]   .user-comment[_ngcontent-%COMP%]   h5[_ngcontent-%COMP%]{display:block;width:100%}"] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PdfLibComponent, [{
        type: Component,
        args: [{ selector: 'kah-pdf-lib', template: "<!--TODO: some kind of tab/styling-->\r\n<div class=\"root\">\r\n  <div class=\"sidebar annotation\" *ngIf=\"annotationsMap && annotationsMap.parentAnnotations\">\r\n    <h1>Annotations</h1>\r\n    <span class=\"file-name\">Some file name</span>\r\n    <ul class=\"comments-list\">\r\n      <li *ngFor=\"let parentAnnotation of annotationsMap.parentAnnotations\">\r\n        <div class=\"user-img\">\r\n<!--          <img src=\"{{baseAvatarUrl+tag.ownerID}}\" alt=\"\">-->\r\n          <img src=\"https://goworks.ergoengineering.com/CM/CM/previewService/get/avatar?id=2377\" alt=\"\">\r\n        </div>\r\n        <div class=\"user-comment\">\r\n          <h5>{{parentAnnotation.annotation.creator.name}}</h5>\r\n          <h4>{{parentAnnotation.annotation.target.selector.subtype}}</h4>\r\n          <em>{{parentAnnotation.annotation.modified |  date:\"dd/MM/yyyy h:mm:ss a z\"}}</em>\r\n          <div>\r\n            <pre>{{parentAnnotation.annotation.bodyValue}}</pre>\r\n          </div>\r\n          <span class=\"curr-status\">\r\n            <!--TODO: some kind of 'can change' security check. Perhaps colour other peoples annotations red too? -->\r\n            <select name=\"choice\" [(ngModel)]=\"parentAnnotation.type\" [disabled]=\"false\"\r\n                    [compareWith]=\"compareAnnotationTypeFn\"\r\n                    (ngModelChange)=\"onChangeSelectedAnnotationType(parentAnnotation)\">\r\n              <option *ngFor=\"let annotationType of annotationTypes\" [ngValue]=\"annotationType\">\r\n                {{annotationType.name}}\r\n              </option>\r\n            </select>\r\n          </span>\r\n          <div *ngIf=\"parentAnnotation.replies.length > 0\">\r\n            Replies\r\n          </div>\r\n          <div *ngFor=\"let reply of parentAnnotation.replies\">\r\n            <h5>{{reply.annotation.creator.name}}</h5>\r\n            <em>{{reply.annotation.modified |  date:\"dd/MM/yyyy h:mm:ss a z\"}}</em>\r\n            <div>\r\n              <pre>{{reply.annotation.bodyValue}}</pre>\r\n            </div>\r\n          </div>\r\n        </div>\r\n      </li>\r\n    </ul>\r\n  </div>\r\n  <div id='pdf-div' class=\"content\"></div>\r\n</div>\r\n<div *ngIf=\"debugOn\">\r\n  <div *ngIf=\"annotationsMap\">\r\n    <div *ngFor=\"let item of annotationsMap.annotationMap | keyvalue: lastUpdatedOrder\">\r\n      Key: {{item.key}}<br/>\r\n      Value: {{item.value | json}}<br/>\r\n      Value2: {{item.value.childIds.size}}\r\n      Value3: {{item.value.childIds | json}}\r\n      Value4:\r\n      <div *ngFor=\"let ci of item.value.childIds\">\r\n        {{ci}}\r\n      </div>\r\n      Value5:\r\n      <div *ngFor=\"let ci of item.value.childIds.keys()\">\r\n        {{ci}}\r\n      </div>\r\n      Value6:\r\n      <div *ngFor=\"let ci of item.value.childIds.values()\">\r\n        {{ci}}\r\n      </div>\r\n      Value7: {{item.value.childIdArray | json}}\r\n    </div>\r\n\r\n    <div>PA count: {{annotationsMap.parentAnnotations.length}}</div>\r\n    <div>annotationsMap: {{annotationsMap.annotationMap }}</div>\r\n    <div *ngFor=\"let parentAnnotation of annotationsMap.parentAnnotations\">\r\n      Key: {{parentAnnotation.annotation.id}}... Modified: {{parentAnnotation.annotation.modified}}\r\n      ... Creator: {{parentAnnotation.annotation.creator.name}}\r\n      ...Text: {{parentAnnotation.annotation.bodyValue}}... Motivation: {{parentAnnotation.annotation.motivation}}\r\n      ...Replies: {{parentAnnotation.replies | json}}\r\n      <select class=\"form-control\" name=\"choice\" [(ngModel)]=\"parentAnnotation.type\"\r\n              [disabled]=\"false\"\r\n              [compareWith]=\"compareAnnotationTypeFn\"\r\n              (ngModelChange)=\"onChangeSelectedAnnotationType(parentAnnotation)\">\r\n        <option *ngFor=\"let annotationType of annotationTypes\"\r\n                [ngValue]=\"annotationType\">\r\n          {{annotationType.name}}\r\n        </option>\r\n      </select>\r\n      <br/>\r\n    </div>\r\n  </div>\r\n</div>\r\n", styles: [".file-name{display:block;padding:7px;text-align:left;position:sticky;top:0;z-index:999}.content{left:385px;padding:40px;background:#eaeaea;border-left:1px solid #d3d3d3;border-top:1px solid #d3d3d3;bottom:0;min-width:670px;overflow:hidden;position:absolute;top:0;transition:all .5s;width:calc(100vw - 466px);z-index:1}.sidebar{background-color:#f8f8f8;color:#4b4b4b;height:100%;overflow-y:auto;padding:25px 20px 0 25px}.sidebar>title{background:#fff;border-bottom:1px solid #d3d3d3;font-size:13px;letter-spacing:1px;padding:28px 0;text-align:center;text-transform:uppercase}.sidebar>main{height:calc(100% - 174px);overflow-y:auto;padding:25px 20px 0 25px}.container{width:385px;border-top:1px solid #d3d3d3;height:calc(100vh - 55px);position:relative}.root{border-top:1px solid #d3d3d3;height:calc(100vh - 55px);position:relative;width:385px}.annotation .comments-list{float:left;width:100%!important;margin:0;padding:0 0 5%;list-style:none;flex-direction:column}.annotation .comments-list li{float:left;width:100%;padding:5px;margin-top:10px;border-bottom:solid 1px #dadde1;position:relative}.annotation .comments-list li .user-img{float:left;width:45px;height:45px;border-radius:50%;overflow:hidden}.annotation .comments-list li .user-comment{width:80%!important;margin-bottom:5px;position:relative;display:inline-block;padding:10px 0 0 10px}.annotation .comments-list li .user-comment h5{display:block;width:100%;font-size:15px;color:#263238;font-weight:600;text-transform:capitalize;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin:0 0 3px}.annotation .comments-list li .user-comment em{display:block;font-size:12px;color:var(--color-medium-grey);font-style:italic;text-align:left}.annotation .comments-list li .user-comment p{display:block;font-size:13px;color:#767676;line-height:15px;word-wrap:break-word}.annotation .comments-list li pre{display:block;font-size:14px;color:#767676;line-height:16px;word-wrap:break-word;margin:10px 0}.annotation .comments-list li .user-comment .edit-btn1{position:absolute;top:20px;right:0;margin:0;font-size:18px;color:#767676;word-wrap:break-word;cursor:pointer}.annotation .comments-list li .user-comment .edit-btn1 span i{color:#000;font-size:medium}.annotation .comments-list li .user-comment #commentSpan{margin:0!important;position:static!important}.annotation .comments-list li .user-comment .save-reposition-active{position:static;top:inherit;left:inherit}.annotation .comments-list li .user-comment .status{float:left;font-size:12px;color:var(--color-white);padding:5px 10px;line-height:12px;border:none;border-radius:0;width:auto;height:auto}.annotation .comments-list li .user-comment .status.green{background:#2eb650}.annotation .comments-list li .user-comment .status.grey{background:var(--color-medium-grey)}.annotation .comments-list li .user-comment+div{flex:0 0 100%;padding:5px;margin-bottom:10px;font-weight:700!important}.annotation .comments-list li .user-comment .col-md-7{width:100%;padding-left:0}.annotation .comments-list li.active .user-img{border:solid 2px var(--primary);margin-bottom:10px;position:relative}.annotation .comments-list li.active .user-img img{height:100%;margin:auto;position:absolute;left:50%;transform:translate(-50%)}.annotation .comments-list li.active .user-comment h5{color:var(--primary);text-align:left}.annotation .comments-list li .curr-status{position:absolute;right:5px;top:-5px;font-size:12px;color:#263238;padding:0}.annotation .comments-list li .curr-status select{border:solid 1px #ccc}.annotation .comments-list li.moderation-comment .user-comment h5{display:block;width:100%}\n"] }]
    }], function () { return [{ type: ViewerService }, { type: PDFLibConfig }]; }, { annotationAdded: [{
            type: Output
        }], annotationUpdated: [{
            type: Output
        }], annotationDeleted: [{
            type: Output
        }], annotationSelectedNotMine: [{
            type: Output
        }], annotationUpdateUndone: [{
            type: Output
        }], annotationDeleteUndone: [{
            type: Output
        }], annotationEvent: [{
            type: Output
        }], annotationError: [{
            type: Output
        }], operationsInfo: [{
            type: Output
        }], annotationTypeChanged: [{
            type: Output
        }], annotationTypes: [{
            type: Input
        }], fileInfo: [{
            type: Input
        }], annotationsToLoad: [{
            type: Input
        }], userProfile: [{
            type: Input
        }] }); })();

class PdfLibModule {
    static forRoot(configuration) {
        return {
            ngModule: PdfLibModule,
            providers: [
                {
                    provide: PDFLibConfig,
                    useValue: configuration
                }
            ]
        };
    }
}
/** @nocollapse */ PdfLibModule.ɵfac = function PdfLibModule_Factory(t) { return new (t || PdfLibModule)(); };
/** @nocollapse */ PdfLibModule.ɵmod = /** @pureOrBreakMyCode */ i0.ɵɵdefineNgModule({ type: PdfLibModule });
/** @nocollapse */ PdfLibModule.ɵinj = /** @pureOrBreakMyCode */ i0.ɵɵdefineInjector({ imports: [FormsModule,
        CommonModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(PdfLibModule, [{
        type: NgModule,
        args: [{
                declarations: [PdfLibComponent],
                imports: [
                    FormsModule,
                    CommonModule
                ],
                exports: [PdfLibComponent]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(PdfLibModule, { declarations: [PdfLibComponent], imports: [FormsModule,
        CommonModule], exports: [PdfLibComponent] }); })();

class Annotation {
    constructor(values) {
        Object.assign(this, values);
        if (values) {
            //
        }
    }
    get isComment() {
        return this.motivation === 'commenting';
    }
    get isReply() {
        return this.motivation === 'replying';
    }
    isITargetNode(toBeDetermined) {
        return toBeDetermined.selector !== undefined;
        // Note we could also rely on this.isComment === true
    }
    get targetNode() {
        if (this.isITargetNode(this.target)) {
            return this.target;
        }
        else {
            return null;
        }
    }
    get targetReply() {
        if (!this.isITargetNode(this.target)) {
            return this.target;
        }
        else {
            return null;
        }
    }
    get pageNumber() {
        // Note that these page numbers start at 0 and are only relevant for comments, although we return 0 for a reply
        return (this.isITargetNode(this.target) ? this.targetNode.selector.node.index : 0);
    }
    // Four bounding box coordinates define a box
    get xMin() {
        // This property is only relevant for comments although we're returning a 0 for a reply here.
        return (this.isITargetNode(this.target) ? this.targetNode.selector.boundingBox[0] : 0);
    }
    get yMin() {
        // This property is only relevant for comments although we're returning a 0 for a reply here.
        return (this.isITargetNode(this.target) ? this.targetNode.selector.boundingBox[1] : 0);
    }
    get xMax() {
        // This property is only relevant for comments although we're returning a 0 for a reply here.
        return (this.isITargetNode(this.target) ? this.targetNode.selector.boundingBox[2] : 0);
    }
    get yMax() {
        // This property is only relevant for comments although we're returning a 0 for a reply here.
        return (this.isITargetNode(this.target) ? this.targetNode.selector.boundingBox[3] : 0);
    }
    get source() {
        return this.target.source;
    }
}

class Selector {
    constructor(values) {
        Object.assign(this, values);
    }
}

/*
 * Public API Surface of pdf-lib
 */

/**
 * Generated bundle index. Do not edit.
 */

export { Annotation, ApiEvent, PDFLibConfig, PdfLibComponent, PdfLibModule, PdfLibService, SelectedAnnotation, Selector, ViewerService };
